/**
 * Marcações da revisão: compara o texto que o editor enviou para revisão
 * (`@alteracoes-revisao`, backend `revisao_base.py`) com o atual. O que os
 * revisores incluíram ou trocaram ganha o marca-texto amarelo; o que excluíram
 * volta ao lugar onde estava, riscado e em vermelho claro.
 *
 * O diff é por palavra (a pontuação conta à parte): marca a palavra inteira,
 * nunca meia palavra.
 */

/** Propriedades de folha do Slate pintadas pelos leafs de `config/blocks/enhancers/slate`. */
export const LEAF_ALTERADO = 'mpfAlterado';
export const LEAF_REMOVIDO = 'mpfRemovido';

/** Acima disso (palavras antigas × novas) o bloco é marcado inteiro. */
const LIMITE_CELULAS = 4_000_000;

export type Trecho = { texto: string; alterado: boolean; removido?: boolean };
type Intervalo = [number, number];

/** Texto excluído e a posição, no texto atual, onde ele estava. */
export type Remocao = { posicao: number; texto: string };

export type ResultadoDiff = {
  /** Intervalos de caracteres do texto novo que foram incluídos ou trocados. */
  intervalos: Intervalo[];
  remocoes: Remocao[];
};

// Palavra (letras e dígitos), espaço ou um sinal de pontuação por vez: trocar
// só o número do processo marca só o trecho do número.
const tokenizar = (texto: string): string[] =>
  texto.match(/\s+|[\p{L}\p{N}]+|[^\s\p{L}\p{N}]/gu) || [];
const ehEspaco = (token: string) => /^\s+$/.test(token);

/**
 * Formatação de cada caractere (link, negrito...), alinhada ao texto. Palavra
 * que continua igual mas mudou de formatação também é alteração: transformar
 * um trecho em link não muda o texto.
 */
export type Estilos = { antigo: string[]; novo: string[] };

const mesmoEstilo = (
  estilos: Estilos,
  deA: number,
  deB: number,
  tamanho: number,
) => {
  for (let k = 0; k < tamanho; k++) {
    if (estilos.antigo[deA + k] !== estilos.novo[deB + k]) return false;
  }
  return true;
};

/** Diff por palavra entre o texto de partida e o atual. */
export const diffPorPalavra = (
  antigo: string,
  novo: string,
  estilos?: Estilos,
): ResultadoDiff => {
  if (
    antigo === novo &&
    (!estilos || mesmoEstilo(estilos, 0, 0, novo.length))
  ) {
    return { intervalos: [], remocoes: [] };
  }
  const a = tokenizar(antigo);
  const b = tokenizar(novo);
  if (a.length * b.length > LIMITE_CELULAS) {
    return {
      intervalos: [[0, novo.length]],
      remocoes: [{ posicao: 0, texto: antigo }],
    };
  }

  // LCS clássico de trás para frente: lcs[i][j] = maior subsequência comum de a[i:] e b[j:].
  const largura = b.length + 1;
  const lcs = new Uint32Array((a.length + 1) * largura);
  for (let i = a.length - 1; i >= 0; i--) {
    for (let j = b.length - 1; j >= 0; j--) {
      lcs[i * largura + j] =
        a[i] === b[j]
          ? lcs[(i + 1) * largura + j + 1] + 1
          : Math.max(lcs[(i + 1) * largura + j], lcs[i * largura + j + 1]);
    }
  }

  const intervalos: Intervalo[] = [];
  const remocoes: Remocao[] = [];
  let removendo: Remocao | null = null;
  let offset = 0;
  let offsetAntigo = 0;
  let i = 0;
  let j = 0;
  const marcar = (inicio: number, fim: number) => {
    const ultimo = intervalos[intervalos.length - 1];
    // Une com o anterior quando só há espaço entre eles: "trecho novo" vira um marcador só.
    if (ultimo && /^\s*$/.test(novo.slice(ultimo[1], inicio))) ultimo[1] = fim;
    else intervalos.push([inicio, fim]);
  };
  const fecharRemocao = () => {
    if (removendo && removendo.texto.trim()) {
      remocoes.push({
        ...removendo,
        texto: removendo.texto.replace(/\s*\n\s*/g, ' '),
      });
    }
    removendo = null;
  };
  while (i < a.length || j < b.length) {
    if (i < a.length && j < b.length && a[i] === b[j]) {
      fecharRemocao();
      if (
        estilos &&
        !ehEspaco(b[j]) &&
        !mesmoEstilo(estilos, offsetAntigo, offset, b[j].length)
      ) {
        marcar(offset, offset + b[j].length);
      }
      offsetAntigo += a[i].length;
      offset += b[j].length;
      i += 1;
      j += 1;
    } else if (
      i < a.length &&
      (j >= b.length || lcs[(i + 1) * largura + j] >= lcs[i * largura + j + 1])
    ) {
      // Remoção antes da inclusão: na troca, o texto antigo riscado vem primeiro.
      if (!removendo) removendo = { posicao: offset, texto: '' };
      removendo.texto += a[i];
      offsetAntigo += a[i].length;
      i += 1;
    } else {
      fecharRemocao();
      if (!ehEspaco(b[j])) marcar(offset, offset + b[j].length);
      offset += b[j].length;
      j += 1;
    }
  }
  fecharRemocao();
  return { intervalos, remocoes };
};

/** Texto em trechos (normal, alterado, removido), para título e descrição. */
export const trechosAlterados = (antigo: string, novo: string) => {
  const atual = novo || '';
  const { intervalos, remocoes } = diffPorPalavra(antigo || '', atual);
  const trechos: Trecho[] = [];
  const pontos = new Set<number>([0, atual.length]);
  intervalos.forEach(([inicio, fim]) => {
    pontos.add(inicio);
    pontos.add(fim);
  });
  remocoes.forEach((r) => pontos.add(r.posicao));
  const ordenados = [...pontos].sort((x, y) => x - y);
  ordenados.forEach((ponto, indice) => {
    remocoes
      .filter((r) => r.posicao === ponto)
      .forEach((r) =>
        trechos.push({ texto: r.texto, alterado: false, removido: true }),
      );
    const proximo = ordenados[indice + 1];
    if (proximo === undefined || proximo === ponto) return;
    trechos.push({
      texto: atual.slice(ponto, proximo),
      alterado: intervalos.some(([x, y]) => x <= ponto && proximo <= y),
    });
  });
  return {
    trechos,
    alterados: intervalos.length,
    removidos: remocoes.length,
  };
};

// ------------------------------------------------------------------ Slate

type SlateNode = { text?: string; children?: SlateNode[]; [k: string]: any };

/** Texto do valor Slate; parágrafos de nível superior separados por quebra. */
export const textoDoSlate = (valor: SlateNode[] = []): string => {
  const doNo = (no: SlateNode): string =>
    typeof no.text === 'string'
      ? no.text
      : (no.children || []).map(doNo).join('');
  return (valor || []).map(doNo).join('\n');
};

/** Assinatura de um nó: tipo e dados do elemento, ou as marcas da folha. */
const assinatura = (no: SlateNode) =>
  JSON.stringify(
    Object.keys(no)
      .filter(
        (k) =>
          k !== 'text' &&
          k !== 'children' &&
          k !== LEAF_ALTERADO &&
          k !== LEAF_REMOVIDO,
      )
      .sort()
      .map((k) => [k, no[k]]),
  );

/** Formatação de cada caractere de `textoDoSlate` (elementos ancestrais + marcas). */
export const estilosDoSlate = (valor: SlateNode[] = []): string[] => {
  const estilos: string[] = [];
  const percorrer = (no: SlateNode, ancestrais: string) => {
    if (typeof no.text === 'string') {
      const estilo = `${ancestrais}|${assinatura(no)}`;
      for (let k = 0; k < no.text.length; k++) estilos.push(estilo);
      return;
    }
    const proprio = `${ancestrais}/${assinatura(no)}`;
    (no.children || []).forEach((filho) => percorrer(filho, proprio));
  };
  (valor || []).forEach((no, indice) => {
    if (indice > 0) estilos.push(''); // a quebra de textoDoSlate
    percorrer(no, '');
  });
  return estilos;
};

/**
 * Copia o valor Slate partindo as folhas: as partes alteradas ganham
 * `mpfAlterado`, e o texto excluído entra como folha `mpfRemovido` na posição
 * em que estava.
 */
const marcarSlate = (
  valor: SlateNode[],
  { intervalos, remocoes }: ResultadoDiff,
): SlateNode[] => {
  let offset = 0;
  const pendentes = [...remocoes];
  const partirFolha = (folha: SlateNode): SlateNode[] => {
    const texto = folha.text || '';
    const inicio = offset;
    const fim = offset + texto.length;
    offset = fim;
    const aqui = pendentes.filter(
      (r) => r.posicao >= inicio && r.posicao <= fim,
    );
    const cortes = intervalos.filter(([x, y]) => x < fim && y > inicio);
    if (!aqui.length && !cortes.length) return [folha];
    aqui.forEach((r) => pendentes.splice(pendentes.indexOf(r), 1));

    const pontos = new Set<number>([inicio, fim]);
    cortes.forEach(([x, y]) => {
      pontos.add(Math.max(x, inicio));
      pontos.add(Math.min(y, fim));
    });
    aqui.forEach((r) => pontos.add(r.posicao));
    const ordenados = [...pontos].sort((x, y) => x - y);

    const partes: SlateNode[] = [];
    ordenados.forEach((ponto, indice) => {
      aqui
        .filter((r) => r.posicao === ponto)
        .forEach((r) => partes.push({ text: r.texto, [LEAF_REMOVIDO]: true }));
      const proximo = ordenados[indice + 1];
      if (proximo === undefined || proximo === ponto) return;
      const parte: SlateNode = {
        ...folha,
        text: texto.slice(ponto - inicio, proximo - inicio),
      };
      if (cortes.some(([x, y]) => x <= ponto && proximo <= y)) {
        parte[LEAF_ALTERADO] = true;
      }
      partes.push(parte);
    });
    return partes;
  };
  const mapear = (no: SlateNode): SlateNode[] =>
    typeof no.text === 'string'
      ? partirFolha(no)
      : [{ ...no, children: (no.children || []).flatMap(mapear) }];
  return (valor || []).map((no, indice) => {
    if (indice > 0) offset += 1; // a quebra de textoDoSlate
    return mapear(no)[0];
  });
};

/** Bloco de texto excluído inteiro: todas as folhas riscadas. */
const riscarSlate = (valor: SlateNode[]): SlateNode[] => {
  const mapear = (no: SlateNode): SlateNode =>
    typeof no.text === 'string'
      ? { ...no, [LEAF_REMOVIDO]: true }
      : { ...no, children: (no.children || []).map(mapear) };
  return (valor || []).map(mapear);
};

// ----------------------------------------------------------------- blocos

type Blocos = Record<string, any>;
type Layout = { items?: string[] };

export type Base = {
  title?: string;
  description?: string;
  blocks?: Blocos;
  blocks_layout?: Layout;
  data?: string;
};

export type Marcacoes = {
  titulo: Trecho[] | null;
  descricao: Trecho[] | null;
  blocks: Blocos;
  blocks_layout: Layout;
  alterados: number;
  removidos: number;
};

const ehTexto = (bloco: any) =>
  bloco?.['@type'] === 'slate' && Array.isArray(bloco.value);

/**
 * Marca título, descrição e blocos de texto do conteúdo atual em relação à
 * base. Bloco de texto excluído volta riscado no lugar onde estava; blocos que
 * não são texto (imagem, vídeo...) entram só na contagem.
 */
export const marcarAlteracoes = (
  conteudo: {
    title?: string;
    description?: string;
    blocks?: Blocos;
    blocks_layout?: Layout;
  },
  base: Base,
): Marcacoes => {
  let alterados = 0;
  let removidos = 0;

  const titulo = trechosAlterados(base.title || '', conteudo.title || '');
  const descricao = trechosAlterados(
    base.description || '',
    conteudo.description || '',
  );
  alterados += titulo.alterados + descricao.alterados;
  removidos += titulo.removidos + descricao.removidos;

  const blocosBase = base.blocks || {};
  const blocks: Blocos = { ...(conteudo.blocks || {}) };
  const atuais = conteudo.blocks_layout?.items || Object.keys(blocks);
  atuais.forEach((id) => {
    const bloco = blocks[id];
    const anterior = blocosBase[id];
    if (!bloco) return;
    if (ehTexto(bloco)) {
      const valorAntes = ehTexto(anterior) ? anterior.value : [];
      const diff = diffPorPalavra(
        textoDoSlate(valorAntes),
        textoDoSlate(bloco.value),
        {
          antigo: estilosDoSlate(valorAntes),
          novo: estilosDoSlate(bloco.value),
        },
      );
      alterados += diff.intervalos.length;
      removidos += diff.remocoes.length;
      if (diff.intervalos.length || diff.remocoes.length) {
        blocks[id] = { ...bloco, value: marcarSlate(bloco.value, diff) };
      }
    } else if (
      !anterior ||
      JSON.stringify(anterior) !== JSON.stringify(bloco)
    ) {
      alterados += 1;
    }
  });

  // Blocos excluídos: os de texto voltam riscados logo após o bloco que os
  // precedia na base (ou no topo); os demais só contam.
  const items = [...atuais];
  let anteriorNaBase: string | null = null;
  (base.blocks_layout?.items || []).forEach((id) => {
    if (blocks[id]) {
      anteriorNaBase = id;
      return;
    }
    removidos += 1;
    const excluido = blocosBase[id];
    if (!ehTexto(excluido) || !textoDoSlate(excluido.value).trim()) return;
    blocks[id] = { ...excluido, value: riscarSlate(excluido.value) };
    items.splice(anteriorNaBase ? items.indexOf(anteriorNaBase) + 1 : 0, 0, id);
    anteriorNaBase = id;
  });

  return {
    titulo: titulo.alterados || titulo.removidos ? titulo.trechos : null,
    descricao:
      descricao.alterados || descricao.removidos ? descricao.trechos : null,
    blocks,
    blocks_layout: { ...(conteudo.blocks_layout || {}), items },
    alterados,
    removidos,
  };
};
